import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';

interface RouteProps {
    path: string;
}

interface PatternPart {
    type: 'static' | 'param';
    value: string;
}

function parsePathPattern(pathPattern: string): PatternPart[] {
    return pathPattern
        .split('/')
        .map(part =>
            part[0] === ':'
                ? { type: 'param', value: part.slice(1) }
                : { type: 'static', value: part },
        );
}

function patternsMatch(pattern: string, provided: string): boolean {
    const patternParts = parsePathPattern(pattern);
    const providedParts = parsePathPattern(provided);

    if (patternParts.length !== providedParts.length) {
        return false;
    }

    for (let i = 0; i < patternParts.length; i++) {
        const patternPart = patternParts[i];
        const providedPart = providedParts[i];

        if (patternPart.type === 'static' && patternPart.value !== providedPart.value) {
            return false;
        }
    }

    return true;
}

function getParams(pattern: string, provided: string): { [key: string]: string } {
    const patternParts = parsePathPattern(pattern);
    const providedParts = parsePathPattern(provided);

    const params: { [key: string]: string } = {};

    for (let i = 0; i < patternParts.length; i++) {
        const patternPart = patternParts[i];
        const providedPart = providedParts[i];

        if (patternPart.type === 'param') {
            params[patternPart.value] = providedPart.value;
        }
    }

    return params;
}

const context = React.createContext<{ path: string }>({
    path: `${window.location.pathname}${window.location.hash}`,
});

function Router({ children }: PropsWithChildren<{}>) {
    const [path, setPath] = useState(`${window.location.pathname}${window.location.hash}`);

    useEffect(() => {
        const hashChangeListener = () => {
            setPath(`${window.location.pathname}${window.location.hash}`);
        };

        window.addEventListener('hashchange', hashChangeListener);

        return () => {
            window.removeEventListener('hashchange', hashChangeListener);
        };
    });

    return <context.Provider value={{ path }}>{children}</context.Provider>;
}

function Route({ path, children }: PropsWithChildren<RouteProps>) {
    return <>{patternsMatch(path, useRouter().path) ? children : null}</>;
}

function useRouter() {
    return useContext(context);
}

export { Router, Route };
export type { RouteProps, PatternPart };
